import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from '@/lib/api';
import { motion, AnimatePresence } from "framer-motion";
import { ChefHat, Send, Loader2, ArrowLeft, CheckCircle2, CreditCard, MessageCircle, Sparkles, Star, User, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API_URL = API_BASE;

const ChefCard = ({ chef, selected, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    whileTap={{ scale: 0.98 }}
    onClick={() => onSelect(chef.user_id)}
    className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${
      selected
        ? "border-amber-500 bg-amber-50 shadow-md"
        : "border-stone-200 bg-white hover:border-amber-300 hover:shadow-sm"
    }`}
    data-testid={`chef-card-${chef.user_id}`}
  >
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {chef.profile_picture ? (
          <img src={chef.profile_picture} alt={chef.name} className="w-full h-full object-cover rounded-full" />
        ) : (
          <User className="w-6 h-6 text-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-emerald-950 truncate">{chef.name}</h4>
        <div className="flex items-center gap-2 text-sm text-stone-500">
          {chef.avg_rating > 0 ? (
            <>
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              <span>{chef.avg_rating.toFixed(1)}</span>
              <span className="text-stone-300">|</span>
              <span>{chef.review_count} reviews</span>
            </>
          ) : (
            <span className="text-stone-400">New professional</span>
          )}
        </div>
      </div>
      {selected && (
        <CheckCircle2 className="w-6 h-6 text-amber-500 flex-shrink-0" />
      )}
    </div>
  </motion.div>
);

const PrivateChefPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bookingReady, setBookingReady] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState("new");

  // Chef selection state
  const [availableChefs, setAvailableChefs] = useState([]);
  const [selectedChefId, setSelectedChefId] = useState(null);
  const [loadingChefs, setLoadingChefs] = useState(false);
  const [chefStep, setChefStep] = useState(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  useEffect(() => {
    const initialMessage = {
      role: "assistant",
      content: "Welcome to Stack's Private Chef service! I'm ChefMatch, your personal culinary concierge. I'll help you find and book the perfect chef for your special event.\n\nTo get started, could you tell me about the occasion? What kind of event are you planning?"
    };
    setMessages([initialMessage]);
  }, []);

  // Fetch available chefs when booking data is ready
  useEffect(() => {
    if (bookingReady && bookingData && !chefStep) {
      fetchAvailableChefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingReady, bookingData]);

  const fetchAvailableChefs = async () => {
    setLoadingChefs(true);
    try {
      const res = await axios.get(`${API_URL}/api/professionals/available?service=private_chef`);
      setAvailableChefs(res.data.professionals || []);
      setChefStep(true);
    } catch (err) {
      console.error("Error fetching chefs:", err);
      toast.error("Could not load available chefs");
      setChefStep(true);
    } finally {
      setLoadingChefs(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: "user", content: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chef/chat`, {
        session_id: sessionId,
        message: inputValue,
        user_email: user?.email || "guest@stack.com",
        user_name: user?.name || "Guest",
        user_phone: user?.phone || null,
        user_address: user?.address || null
      });

      setSessionId(response.data.session_id);
      
      let displayText = response.data.response;
      displayText = displayText.replace(/```json[\s\S]*?```/g, "").trim();
      
      const assistantMessage = { role: "assistant", content: displayText };
      setMessages(prev => [...prev, assistantMessage]);

      if (response.data.booking_ready && response.data.booking_data) {
        setBookingReady(true);
        setBookingData(response.data.booking_data);
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage.content);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleConfirmBooking = async () => {
    if (!bookingData || !sessionId) return;
    
    setIsBooking(true);
    try {
      const bookingPayload = {
        session_id: sessionId,
        event_date: bookingData.event_date,
        event_time: bookingData.event_time,
        guests: parseInt(bookingData.guests) || 2,
        cuisine: bookingData.cuisine || "Not specified",
        service_address: bookingData.service_address || user?.address || "Not specified",
        customer_name: user?.name || bookingData.customer_name || "Guest",
        customer_phone: user?.phone || bookingData.customer_phone || "Not provided",
        customer_email: user?.email || "guest@stack.com",
        budget_range: bookingData.budget_range || "moderate",
        dietary_restrictions: bookingData.dietary_restrictions !== "none" ? bookingData.dietary_restrictions : null,
        special_occasion: bookingData.special_occasion !== "none" ? bookingData.special_occasion : null,
        special_requests: bookingData.special_requests !== "none" ? bookingData.special_requests : null,
        selected_professional_id: selectedChefId || null,
      };

      const response = await axios.post(`${API_URL}/api/chef/booking`, bookingPayload);
      
      toast.success("Booking confirmed! Processing payment...");
      
      const guests = bookingPayload.guests || 2;
      const ratePerGuest = 65;
      const totalPrice = Math.max(guests * ratePerGuest, 150);
      
      const serviceData = {
        booking_id: response.data.id,
        booking_type: "chef",
        amount: totalPrice,
        customer_email: bookingPayload.customer_email,
        customer_name: bookingPayload.customer_name,
        service_type: "private_chef",
        pickup_address: bookingPayload.service_address || "",
        dropoff_address: "",
        targeted_professional_id: selectedChefId || null,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API_URL}/api/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your chef...");
          navigate("/");
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setIsBooking(false);
      } else {
        const paymentResponse = await axios.post(`${API_URL}/api/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Booking error:", error);
      console.error("Error response:", error.response?.data);
      let errorMsg = "Failed to create booking. Please try again.";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setIsBooking(false);
    }
  };

  const selectedChef = availableChefs.find(c => c.user_id === selectedChefId);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white" data-testid="private-chef-page">
      <Navbar />

      <section className="pt-28 pb-8 px-4 md:px-0">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-4 text-stone-600 hover:text-emerald-700"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <ChefHat className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-emerald-950">Private Chef</h1>
              <p className="text-stone-600">Book a personal chef for your special event</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { icon: MessageCircle, text: "AI-Powered Booking" },
              { icon: Sparkles, text: "Personalized Match" },
              { icon: CheckCircle2, text: "Verified Chefs" }
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-stone-600">
                <feature.icon className="w-4 h-4 text-amber-500" />
                <span>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 px-4 md:px-0">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-stone-200 overflow-hidden" data-testid="chat-container">
            <div className="bg-gradient-to-r from-emerald-900 to-emerald-800 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-emerald-950" />
              </div>
              <div>
                <h3 className="font-semibold text-white">ChefMatch</h3>
                <p className="text-xs text-emerald-200">Your Personal Culinary Concierge</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-xs text-emerald-200">Online</span>
              </div>
            </div>

            <div className="h-[450px] overflow-y-auto p-6 space-y-4 bg-stone-50" data-testid="messages-area">
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-emerald-600 text-white rounded-br-md"
                          : "bg-white text-stone-800 shadow-sm border border-stone-100 rounded-bl-md"
                      }`}
                      data-testid={`message-${message.role}`}
                    >
                      <p className="whitespace-pre-wrap text-sm md:text-base">{message.content}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                      <span className="text-sm text-stone-500">ChefMatch is typing...</span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Chef Selection Step */}
            {bookingReady && bookingData && chefStep && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="bg-gradient-to-r from-amber-50 to-orange-50 border-t border-amber-200 p-6"
                data-testid="chef-selection-section"
              >
                <div className="mb-4">
                  <h4 className="font-semibold text-emerald-950 mb-1 flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-amber-500" />
                    Choose Your Chef
                  </h4>
                  <p className="text-sm text-stone-500">
                    Select a chef you prefer, or skip to let us match you automatically.
                  </p>
                </div>

                {loadingChefs ? (
                  <div className="flex items-center justify-center py-8 gap-2" data-testid="loading-chefs">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    <span className="text-sm text-stone-500">Finding available chefs...</span>
                  </div>
                ) : availableChefs.length > 0 ? (
                  <div className="space-y-3 max-h-[250px] overflow-y-auto mb-4">
                    {availableChefs.map((chef) => (
                      <ChefCard
                        key={chef.user_id}
                        chef={chef}
                        selected={selectedChefId === chef.user_id}
                        onSelect={setSelectedChefId}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 mb-4" data-testid="no-chefs-available">
                    <p className="text-stone-500 text-sm mb-2">No chefs are online right now.</p>
                    <p className="text-stone-400 text-xs">Your request will be broadcast to all available chefs after payment.</p>
                  </div>
                )}

                {selectedChefId && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-amber-100 rounded-lg" data-testid="selected-chef-badge">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-800 font-medium">
                      Selected: {selectedChef?.name}
                    </span>
                    <button
                      onClick={() => setSelectedChefId(null)}
                      className="ml-auto text-xs text-amber-600 hover:text-amber-800 underline"
                      data-testid="deselect-chef-btn"
                    >
                      Remove
                    </button>
                  </div>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAvailableChefs}
                  className="text-amber-600 hover:text-amber-800 mb-4"
                  data-testid="refresh-chefs-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Refresh list
                </Button>
              </motion.div>
            )}

            {/* Booking Confirmation Panel */}
            {bookingReady && bookingData && chefStep && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="bg-gradient-to-r from-amber-50 to-orange-50 border-t border-amber-200 p-6"
                data-testid="booking-confirmation"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-emerald-950 mb-2">Ready to Confirm Your Booking!</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-stone-600 mb-4">
                      <div><strong>Date:</strong> {bookingData.event_date}</div>
                      <div><strong>Time:</strong> {bookingData.event_time}</div>
                      <div><strong>Guests:</strong> {bookingData.guests}</div>
                      <div><strong>Cuisine:</strong> {bookingData.cuisine}</div>
                      {selectedChef && (
                        <div className="col-span-2">
                          <strong>Chef:</strong> {selectedChef.name}
                          {selectedChef.avg_rating > 0 && ` (${selectedChef.avg_rating.toFixed(1)} stars)`}
                        </div>
                      )}
                    </div>
                    <SavedCardsSelector
                      email={user?.email}
                      name={user?.name}
                      onCardSelect={setSelectedCardId}
                      selectedCardId={selectedCardId}
                      disabled={isBooking}
                    />
                    <Button
                      onClick={handleConfirmBooking}
                      disabled={isBooking}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      data-testid="confirm-booking-btn"
                    >
                      {isBooking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {selectedChefId ? "Confirm & Request Chef" : "Confirm & Pay"}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            <form onSubmit={sendMessage} className="border-t border-stone-200 p-4 bg-white">
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  autoFocus
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 rounded-full border-stone-300 focus:border-amber-400 focus:ring-amber-400"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                  data-testid="send-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-stone-400 mt-2 text-center">
                Powered by AI • Your data is secure and private
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateChefPage;
